import * as d3 from 'd3';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { VISUALIZATION_CONFIG } from '../config';

type ZoomConfig = {
  /** Minimum zoom scale */
  min: number;
  /** Maximum zoom scale */
  max: number;
};

type Transform = {
  /** Scale factor */
  k: number;
  /** X translation */
  x: number;
  /** Y translation */
  y: number;
};

type UseZoomPanProps = {
  /** Reference to the SVG element */
  svgRef: RefObject<SVGSVGElement | null>;
  /** Zoom configuration */
  zoom?: ZoomConfig;
  /** Whether zoom/pan is enabled */
  enabled?: boolean;
};

type UseZoomPanReturn = {
  /** Current transform state (k, x, y) */
  transform: Transform;
  /** Transform as CSS string for use in SVG */
  transformString: string;
  /** Reset zoom to identity (scale 1, no translation) */
  resetZoom: () => void;
};

const IDENTITY_TRANSFORM: Transform = { k: 1, x: 0, y: 0 };

/**
 * Hook to manage zoom/pan behavior with React state.
 * Unlike the imperative approach, this stores transform in React state
 * so it can be applied declaratively via JSX.
 *
 * @example
 * ```tsx
 * const { transformString, resetZoom } = useZoomPan({ svgRef });
 *
 * return (
 *   <svg ref={svgRef} viewBox={viewBox}>
 *     <g transform={transformString}>
 *       {children}
 *     </g>
 *   </svg>
 * );
 * ```
 */
const useZoomPan = ({
  svgRef,
  zoom = VISUALIZATION_CONFIG.ZOOM,
  enabled = true,
}: UseZoomPanProps): UseZoomPanReturn => {
  const [transform, setTransform] = useState<Transform>(IDENTITY_TRANSFORM);
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  // Batch zoom updates to once per animation frame
  const rafIdRef = useRef<number | null>(null);
  const pendingTransformRef = useRef<Transform>(IDENTITY_TRANSFORM);

  // Reset any zoom/pan that might be applied to the SVG
  const resetZoom = useCallback(() => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
  }, [svgRef]);

  // Setup zoom behavior
  useEffect(() => {
    if (!svgRef.current || !enabled) return;

    const svg = d3.select(svgRef.current);

    const zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([zoom.min, zoom.max])
      .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
        pendingTransformRef.current = {
          k: event.transform.k,
          x: event.transform.x,
          y: event.transform.y,
        };

        if (rafIdRef.current !== null) return;

        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          const next = pendingTransformRef.current;

          setTransform((prev) => {
            if (prev.k === next.k && prev.x === next.x && prev.y === next.y) return prev;
            return next;
          });
        });
      });

    zoomBehaviorRef.current = zoomBehavior;
    svg.call(zoomBehavior);

    // Cleanup
    return () => {
      svg.on('.zoom', null); // Remove zoom listeners
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      zoomBehaviorRef.current = null;
    };
  }, [svgRef, zoom.min, zoom.max, enabled]);

  // Compute transform string for SVG
  const transformString = `translate(${transform.x}, ${transform.y}) scale(${transform.k})`;

  return {
    transform,
    transformString,
    resetZoom,
  };
};

export { type Transform, type UseZoomPanProps, type UseZoomPanReturn, useZoomPan };
